import { useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { useStripeCheckout } from './useStripeCheckout'
import { useVoucher } from './useVoucher'

const FULL_PRICE_EUR = 39.9

type PaywallProps = {
  /** 0 = verborgen, 1 = voll sichtbar und bedienbar. Kommt aus useSciencePaywall. */
  opacity: number
  /** Ziel, zu dem Stripe nach erfolgreicher Zahlung zurückkehrt. */
  chapterSlug: string
  /** Ziel des Zurück-Knopfes. Vorgabe: die Übersicht der Nutzerin. */
  onBack?: () => void
}

/**
 * Die Bezahlschranke: verdunkelter Hintergrund, Karte mit Preis, Gutschein und
 * Stripe-Checkout. Beide Ebenen hängen per Portal direkt an <body> - die Leseansichten
 * sind eigene Stacking-Contexts (fixed + z-index), in denen selbst z-[10000] die
 * Bottom-Nav nicht überlagern könnte.
 *
 * Eingehängt wird die Komponente nur, wenn der Bezahlstatus als `false` feststeht;
 * ob sie zu sehen ist, entscheidet `opacity`.
 */
export function Paywall({ opacity, chapterSlug, onBack }: PaywallProps) {
  const navigate = useNavigate()
  const [showVoucherOverlay, setShowVoucherOverlay] = useState(false)
  const {
    code: voucherCode,
    setCode: setVoucherCode,
    discount: voucherDiscount,
    error: voucherError,
    isValidating: validatingVoucher,
    validate: validateVoucher,
  } = useVoucher()
  const {
    startCheckout,
    isLoading: loadingCheckout,
    error: checkoutError,
  } = useStripeCheckout(chapterSlug)

  const handleRedeem = async () => {
    if (await validateVoucher(voucherCode)) setShowVoucherOverlay(false)
  }

  const interactive = opacity === 1
  const discountedPrice = voucherDiscount
    ? (FULL_PRICE_EUR * (1 - voucherDiscount / 100)).toFixed(2).replace('.', ',')
    : null

  return (
    <>
      {opacity > 0 &&
        createPortal(
          <div
            className="fixed left-0 right-0 top-0 bottom-20 sm:bottom-16 z-[9999] bg-black/50"
            style={{
              opacity,
              transition: 'opacity 0.4s ease',
              pointerEvents: interactive ? 'auto' : 'none',
            }}
          />,
          document.body
        )}

      {createPortal(
        <div
          className={[
            // Reicht vom oberen Viewport-Rand bis zur Oberkante der Bottom-Nav (h-20,
            // ab sm h-16) - die Karte sitzt in diesem Bereich mittig.
            'fixed left-0 right-0 top-0 bottom-20 sm:bottom-16 z-[10000]',
            'flex items-center justify-center px-4 py-6',
          ].join(' ')}
          style={{
            opacity,
            transition: 'opacity 0.4s ease',
            pointerEvents: interactive ? 'auto' : 'none',
          }}
        >
          <div className="w-full max-w-[480px] max-h-full overflow-y-auto bg-white dark:bg-[hsl(17,18%,27%)] border border-border dark:border-transparent rounded-2xl shadow-[0_0_40px_rgba(80,50,20,0.15)] flex flex-col items-center px-7 py-8 text-center">
            <div className="mb-4 w-11 h-11 rounded-full bg-accent dark:bg-primary flex items-center justify-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-5 h-5 text-foreground dark:text-white"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </div>

            <h2
              className="text-xl font-semibold mb-4 text-foreground dark:text-white leading-snug"
              style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
            >
              Zugang freischalten
            </h2>

            <div className="text-sm text-foreground/80 leading-relaxed space-y-3 mb-5 max-w-xs text-center">
              <p>
                Schön, dass du dich intensiver mit deiner Stillvorbereitung beschäftigen möchtest.
              </p>
              <p>
                Der weiterführende, personalisierte Inhalt steht dir nach Freischaltung zur
                Verfügung. Damit erhältst du Zugang zu fundiertem Wissen, individuellen
                Einschätzungen und konkreten Empfehlungen, die dich optimal auf deine Stillzeit
                vorbereiten.
              </p>
            </div>

            <div className="w-full max-w-xs bg-white dark:bg-[hsl(17,18%,27%)] rounded-xl p-2 mb-5 text-center">
              <div className="text-2xl font-semibold text-foreground mb-1">
                {discountedPrice ? (
                  <>
                    <span className="line-through text-foreground/40 text-lg mr-2">39,90 €</span>
                    <span className="text-green-600">{discountedPrice} €</span>
                  </>
                ) : (
                  '39,90 €'
                )}
              </div>
              <div className="text-xs text-foreground/60">einmaliger Zugang für ein Jahr</div>
              {voucherDiscount ? (
                <div className="mt-1">
                  <div className="text-xs text-green-600 font-medium">
                    Gutschein {voucherCode} (-{voucherDiscount}%)
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowVoucherOverlay(true)}
                    className="text-xs text-foreground/50 hover:text-foreground/70 underline mt-1"
                  >
                    Gutschein ändern
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowVoucherOverlay(true)}
                  className="text-xs text-primary hover:text-primary/80 underline mt-1"
                >
                  Gutschein einlösen
                </button>
              )}
            </div>

            {checkoutError && (
              <div className="w-full max-w-xs bg-red-50 border border-red-200 rounded-xl p-3 mb-4 text-center">
                <p className="text-red-600 text-sm">{checkoutError}</p>
              </div>
            )}

            <button
              type="button"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                startCheckout()
              }}
              disabled={loadingCheckout}
              className="w-full max-w-xs bg-primary text-background dark:text-white font-semibold py-3 px-6 rounded-xl hover:opacity-80 active:opacity-70 disabled:opacity-40 disabled:cursor-not-allowed text-sm touch-manipulation select-none"
            >
              {loadingCheckout ? 'Weiterleitung zu Stripe…' : 'Jetzt freischalten'}
            </button>

            <button
              type="button"
              onClick={() => (onBack ? onBack() : navigate('/user'))}
              className="mt-5 text-base text-foreground/60 hover:text-foreground underline"
            >
              Zurück zur Übersicht
            </button>
          </div>
        </div>,
        document.body
      )}

      {/* Gutschein-Dialog - muss wie die Paywall ans <body>, sonst läge er dahinter. */}
      {showVoucherOverlay &&
        createPortal(
          <div
            className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/50"
            onClick={() => setShowVoucherOverlay(false)}
          >
            <div
              className="bg-white dark:bg-card rounded-2xl p-6 mx-4 max-w-sm w-full shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <h3
                className="text-lg font-semibold text-foreground mb-4 text-center"
                style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
              >
                Gutschein einlösen
              </h3>

              <input
                type="text"
                value={voucherCode}
                onChange={(e) => setVoucherCode(e.target.value.toUpperCase())}
                placeholder="Gutscheincode eingeben"
                className="w-full px-4 py-3 border border-border rounded-xl text-center font-mono text-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
              />

              {voucherError && (
                <p className="text-red-500 text-sm mt-2 text-center">{voucherError}</p>
              )}

              {voucherDiscount && (
                <p className="text-green-600 text-sm mt-2 text-center">
                  {voucherDiscount}% Rabatt wird angewendet!
                </p>
              )}

              <div className="flex gap-3 mt-4">
                <button
                  type="button"
                  onClick={() => setShowVoucherOverlay(false)}
                  className="flex-1 px-4 py-2 border border-border rounded-xl text-foreground/70 hover:bg-accent"
                >
                  Abbrechen
                </button>
                <button
                  type="button"
                  onClick={handleRedeem}
                  disabled={validatingVoucher || !voucherCode.trim()}
                  className="flex-1 px-4 py-2 bg-primary text-background rounded-xl hover:opacity-80 disabled:opacity-40"
                >
                  {validatingVoucher ? 'Prüfen...' : 'Einlösen'}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  )
}
